import type { RumInitConfiguration } from '@datadog/browser-rum'

import { REGIONS } from 'app/lib/constants'

import { developmentConfig } from './development.config'
import { productionConfig } from './production.config'
import { productionEuConfig } from './production.eu.config'
import { testConfig } from './test.config'

declare global {
  interface Window {
    PRESTO_CONFIG?: {
      useEphemeralMultipass(prNumber: number): void
      clearEphemeralMultipass(): void
    }
  }
}

export type Config = Readonly<{
  auth0: {
    audience: string
    clientId: string
    domain: string
    connections: Record<string, string>
  }
  launchDarkly: {
    clientId: string
  }
  scribe: {
    baseUrl: string
  }
  multipass: {
    baseUrl: string
  }
  coredata: {
    baseUrl: string
  }
  activeMinutes: {
    baseUrl: string
  }
  stripePublicKey: string
  theme: {
    applicationBar: {
      backgroundColor: string
    }
  }
  assets_url: string
  snowplow: {
    appId: string
    namespace: string
    endpoint: string
  }
  datadog?: Pick<RumInitConfiguration, 'applicationId' | 'clientToken' | 'env' | 'site'>
  zendesk: {
    widgetKey: string
  }
  region: REGIONS
}>

type ReactApplicationEnvironment = 'local' | 'test' | 'dev-ca' | 'prod-ca' | 'prod-eu'

const configs: Record<ReactApplicationEnvironment, Config> = {
  local: developmentConfig,
  test: testConfig,
  'dev-ca': developmentConfig,
  'prod-ca': productionConfig,
  'prod-eu': productionEuConfig,
}

const config = configs[process.env.VITE_APP_ENV as ReactApplicationEnvironment]

const STORAGE_KEY = 'ephemeralMultipassPr'
let ephemeralMultipassPr: number | undefined

const buildEphemeralUrl = (pr: number) => `https://multipass-pr-${pr}.dev.dialoguecorp.com`

// Restore persisted ephemeral PR from sessionStorage
if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
  const stored = sessionStorage.getItem(STORAGE_KEY)
  const parsed = stored ? Number(stored) : undefined

  if (parsed && !Number.isNaN(parsed)) {
    ephemeralMultipassPr = parsed
    config.multipass.baseUrl = buildEphemeralUrl(parsed)
  }
}

export function getApplicationConfig(): Config {
  return config
}

export function getApplicationEnvironment() {
  return process.env.VITE_APP_ENV as ReactApplicationEnvironment
}

export function getMultipassPr(): number | undefined {
  return ephemeralMultipassPr
}

export function setEphemeralMultipass(prNumber: number): void {
  ephemeralMultipassPr = prNumber
  config.multipass.baseUrl = buildEphemeralUrl(prNumber)

  if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(STORAGE_KEY, String(prNumber))
  }
}

export function clearEphemeralMultipass(): void {
  ephemeralMultipassPr = undefined
  config.multipass.baseUrl = 'https://multipass.dev.dialoguecorp.com'

  if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(STORAGE_KEY)
  }
}

// Expose window.PRESTO_CONFIG helper in dev environments
if (
  typeof window !== 'undefined' &&
  (process.env.VITE_APP_ENV?.includes('dev') ||
    process.env.VITE_APP_ENV === 'local' ||
    process.env.VITE_APP_ENV === 'test')
) {
  const helpers = {
    useEphemeralMultipass(prNumber: number) {
      setEphemeralMultipass(prNumber)

      console.log(
        `Configured ephemeral multipass for PR ${prNumber}:`,
        `https://github.com/dialoguemd/multipass/pull/${prNumber}`,
      )
    },
    clearEphemeralMultipass() {
      clearEphemeralMultipass()

      console.log('Cleared ephemeral multipass configuration')
    },
  }

  window.PRESTO_CONFIG = helpers
}
